import { api } from "./api";

/**
 * Standalone video library — `/videos/*`.
 *
 * Separate from health feeds: videos used to be a feed type on the blog screen,
 * stored as `video_links` on a health-feed record. They have their own table and
 * their own endpoints, so they get their own screen.
 *
 * Paths are relative to the API base, which already ends in `/api` — the routes
 * are `/videos/...`, not `/api/videos/...`. Verified 27 Aug 2026: the doubled
 * form answers the API's "Page not found" catch-all.
 */

export interface VideoRecord {
  video_id?: string;
  video_heading?: string;
  video_url?: string;
  /** Banner image. Stored as the root-relative path the upload returns
   *  ("/files/transformation/….png"), or any URL pasted by hand — pass it
   *  through assetSrc() before putting it in an <img>. */
  thumbnail?: string | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * The video list.
 *
 * Two routes, because the keyword one cannot express "no filter":
 * `/getAllVideosByKeyword/` with an empty segment is "Page not found", so an
 * unfiltered listing had to invent a sentinel value and hope the backend read it
 * as "everything". `/videos/getAllVideos` removes that guess — a blank search
 * asks for all videos outright, and only a real keyword takes the search route.
 */
export const GetAllVideosApi = (keyword?: string) => {
  const filterValue = (keyword ?? "").trim();
  if (!filterValue) {
    return api.get(`/videos/getAllVideos`);
  }
  return api.get(`/videos/getAllVideosByKeyword/${encodeURIComponent(filterValue)}`);
};

export const CreateVideoApi = (params: {
  video_heading: string;
  video_url: string;
  thumbnail?: string | null;
}) => {
  return api.post(`/videos/createVideo`, params);
};

export const UpdateVideoApi = (params: {
  video_id: string;
  video_heading: string;
  video_url: string;
  thumbnail?: string | null;
}) => {
  return api.post(`/videos/updateVideo`, params);
};

/** GET, not DELETE — the API exposes removal as `GET /videos/deleteVideo/:id`. */
export const DeleteVideoApi = (videoId: string) => {
  return api.get(`/videos/deleteVideo/${encodeURIComponent(videoId)}`);
};
